/**
 * Direct Auto's margin pricing guide.
 *
 * From Winnie's review — the target uplift Direct Auto expects to make on a
 * vehicle, banded by what the car is worth:
 *
 *   up to $49,999   → +$4,000    (low-end volume)
 *   $50,000–59,999  → +$5,000    (standard)
 *   $60,000–69,999  → +$6,000    (mid-tier)
 *   $70,000–94,999  → +$7,000 to +$9,000 (higher-value)
 *   $95,000+        → +$10,000+  (premium)
 *
 * The previous implementation stored this as flat *percentages* per band, which
 * did not reproduce the table: a $59,999 car came out at 10% ≈ $6,000 where the
 * guide asks for $5,000. Dollars are the source of truth here; a percentage is
 * only ever derived for display.
 *
 * The $70k–$95k band is a range rather than a single figure, so it is
 * interpolated across the band — $70,000 targets +$7,000 and $94,999 targets
 * +$9,000 — and the premium band scales at the same rate it ends on rather than
 * flat-lining at $10,000.
 */

export interface MarginBand {
  /** Inclusive lower bound of the band. */
  min: number
  /** Exclusive upper bound; null for the open-ended top band. */
  max: number | null
  label: string
  /** Human-readable target, e.g. "+$7,000–9,000". */
  display: string
}

export const MARGIN_BANDS: MarginBand[] = [
  { min: 0, max: 50000, label: 'Low-end volume', display: '+$4,000' },
  { min: 50000, max: 60000, label: 'Standard', display: '+$5,000' },
  { min: 60000, max: 70000, label: 'Mid-tier', display: '+$6,000' },
  { min: 70000, max: 95000, label: 'Higher-value', display: '+$7,000–9,000' },
  { min: 95000, max: null, label: 'Premium', display: '+$10,000+' },
]

/** The band a vehicle price falls into. */
export function bandFor(price: number): MarginBand {
  const p = Number(price) || 0
  return MARGIN_BANDS.find((b) => p >= b.min && (b.max === null || p < b.max)) || MARGIN_BANDS[0]
}

/** The target margin in dollars for a given vehicle price. */
export function targetMarginDollars(price: number): number {
  const p = Number(price) || 0
  if (p < 50000) return 4000
  if (p < 60000) return 5000
  if (p < 70000) return 6000
  if (p < 95000) {
    // $70,000 → $7,000 rising to $94,999 → $9,000.
    const progress = (p - 70000) / (95000 - 70000)
    return Math.round((7000 + progress * 2000) / 100) * 100
  }
  // Premium: $10,000 at $95,000, continuing to rise at the same rate.
  return Math.round((10000 + (p - 95000) * 0.08) / 100) * 100
}

/** The same target expressed as a percentage of the price, for display only. */
export function targetMarginPercent(price: number): number {
  const p = Number(price) || 0
  if (p <= 0) return 0
  return (targetMarginDollars(p) / p) * 100
}

export type GuideVerdict = 'above' | 'on' | 'below' | 'unknown'

/**
 * How a vehicle's actual margin compares with the guide.
 *
 * "On guide" allows a 5% tolerance either side of the target so a car $50 short
 * is not flagged as failing.
 */
export function compareToGuide(price: number, actualMarginDollars: number | null | undefined): GuideVerdict {
  if (actualMarginDollars === null || actualMarginDollars === undefined) return 'unknown'
  const target = targetMarginDollars(price)
  if (target <= 0) return 'unknown'
  const ratio = actualMarginDollars / target
  if (ratio >= 1.05) return 'above'
  if (ratio >= 0.95) return 'on'
  return 'below'
}

/** Does this vehicle meet or beat the guide? */
export function meetsGuide(price: number, actualMarginDollars: number | null | undefined): boolean {
  const v = compareToGuide(price, actualMarginDollars)
  return v === 'above' || v === 'on'
}

export const GUIDE_VERDICT_LABEL: Record<GuideVerdict, string> = {
  above: 'Above guide',
  on: 'On guide',
  below: 'Below guide',
  unknown: 'No margin',
}

export const money0 = (n: number) => `$${Math.round(n).toLocaleString('en-AU')}`

/**
 * The most we may pay for a car whose market value is `marketValue`.
 *
 * The band is read off the MARKET VALUE — Winnie's ruling on 24 August. The
 * guide is a target on what the car is worth, so the price is the market value
 * less that target, rounded DOWN to the nearest fifty. Rounding up can leave a
 * price a dollar short of its own target, which is the one outcome this exists
 * to prevent.
 *
 * It used to band on the buying price, which made the sum circular and had to
 * be solved by bisection. It no longer does. `functions/api/_lib/sourcing.js`
 * carries the same rule for the scanner and `qa/marginsuggestion.mjs` checks
 * the two agree.
 *
 * Returns null when there is no usable market value, so callers render nothing
 * rather than a confident zero.
 */
export function suggestedBuyPrice(marketValue: number | null | undefined): number | null {
  const mv = Number(marketValue)
  if (!Number.isFinite(mv) || mv <= 0) return null

  const price = Math.floor((mv - targetMarginDollars(mv)) / 50) * 50
  return price > 0 ? price : null
}

/**
 * How far a purchase price sits above the suggestion. Negative means under it.
 * Null means we cannot tell, which is not the same as zero and must never be
 * rendered as `on the money`.
 */
export function overSuggestion(
  buyPrice: number | null | undefined,
  marketValue: number | null | undefined,
): number | null {
  const suggested = suggestedBuyPrice(marketValue)
  const buy = Number(buyPrice)
  if (suggested === null || !Number.isFinite(buy)) return null
  return buy - suggested
}
